/**
 * Multi-scene registry (localStorage only).
 *
 * The *active* scene always lives in the regular `excalidraw` /
 * `excalidraw-state` keys, so LocalData, tab sync and initial load keep
 * working unchanged. Inactive scenes are parked under
 * `excalidraw-scene-<id>`. Switching swaps data between the two.
 */

import { randomId } from "@excalidraw/common";

import { STORAGE_KEYS } from "../app_constants";

import { importFromLocalStorage } from "./localStorage";
import { updateBrowserStateVersion } from "./tabSync";

export type SceneMeta = {
  id: string;
  name: string;
  updatedAt: number;
};

export type ScenesIndex = {
  scenes: SceneMeta[];
  activeId: string;
};

type ParkedScene = { elements: string | null; appState: string | null };

const sceneKey = (id: string) =>
  `${STORAGE_KEYS.LOCAL_STORAGE_SCENE_PREFIX}${id}`;

const createDefaultIndex = (): ScenesIndex => {
  const id = randomId();
  return {
    scenes: [{ id, name: "Scene 1", updatedAt: Date.now() }],
    activeId: id,
  };
};

const writeIndex = (index: ScenesIndex) => {
  try {
    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_SCENES,
      JSON.stringify(index),
    );
  } catch (error: any) {
    console.error(error);
  }
};

const isValidIndex = (value: any): value is ScenesIndex =>
  value &&
  Array.isArray(value.scenes) &&
  value.scenes.length > 0 &&
  value.scenes.every((s: any) => typeof s?.id === "string") &&
  value.scenes.some((s: any) => s.id === value.activeId);

export const getScenesIndex = (): ScenesIndex => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_SCENES);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (isValidIndex(parsed)) {
        return parsed;
      }
    }
  } catch (error: any) {
    console.error(error);
  }
  // first run (migrates legacy single scene) or corrupted index
  const index = createDefaultIndex();
  writeIndex(index);
  return index;
};

/** copies the active scene's main-key data into its parking slot */
const parkActiveScene = (index: ScenesIndex) => {
  const parked: ParkedScene = {
    elements: localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS),
    appState: localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE),
  };
  localStorage.setItem(sceneKey(index.activeId), JSON.stringify(parked));
  const meta = index.scenes.find((s) => s.id === index.activeId);
  if (meta) {
    meta.updatedAt = Date.now();
  }
};

/** moves a parked scene's data into the main keys (or empties them) */
const loadIntoActiveSlot = (id: string | null) => {
  let parked: ParkedScene | null = null;
  if (id) {
    try {
      const raw = localStorage.getItem(sceneKey(id));
      parked = raw ? JSON.parse(raw) : null;
      localStorage.removeItem(sceneKey(id));
    } catch (error: any) {
      console.error(error);
    }
  }
  const set = (key: string, value: string | null | undefined) => {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  };
  set(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS, parked?.elements);
  set(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE, parked?.appState);
  updateBrowserStateVersion(STORAGE_KEYS.VERSION_DATA_STATE);
};

/**
 * Callers must flush pending LocalData saves *before* mutating the active
 * scene, so main keys hold the latest data.
 */
export const switchScene = (id: string) => {
  const index = getScenesIndex();
  if (id !== index.activeId && index.scenes.some((s) => s.id === id)) {
    parkActiveScene(index);
    loadIntoActiveSlot(id);
    index.activeId = id;
    writeIndex(index);
  }
  return importFromLocalStorage();
};

export const createScene = () => {
  const index = getScenesIndex();
  parkActiveScene(index);
  loadIntoActiveSlot(null);

  const names = new Set(index.scenes.map((s) => s.name));
  let n = index.scenes.length + 1;
  while (names.has(`Scene ${n}`)) {
    n++;
  }
  const scene: SceneMeta = {
    id: randomId(),
    name: `Scene ${n}`,
    updatedAt: Date.now(),
  };
  index.scenes.push(scene);
  index.activeId = scene.id;
  writeIndex(index);
  return { index, scene };
};

export const renameScene = (id: string, name: string) => {
  const trimmed = name.trim();
  const index = getScenesIndex();
  const meta = index.scenes.find((s) => s.id === id);
  if (meta && trimmed) {
    meta.name = trimmed;
    writeIndex(index);
  }
  return index;
};

/**
 * Returns the newly active scene's data if the active scene was deleted,
 * otherwise `null`. The last remaining scene can't be deleted.
 */
export const deleteScene = (id: string) => {
  const index = getScenesIndex();
  const position = index.scenes.findIndex((s) => s.id === id);
  if (position === -1 || index.scenes.length === 1) {
    return null;
  }
  index.scenes.splice(position, 1);

  if (id === index.activeId) {
    const next = index.scenes[Math.min(position, index.scenes.length - 1)];
    // the deleted scene's data is discarded, not parked
    loadIntoActiveSlot(next.id);
    index.activeId = next.id;
    writeIndex(index);
    return importFromLocalStorage();
  }

  localStorage.removeItem(sceneKey(id));
  writeIndex(index);
  return null;
};
